import { useEffect, useRef, useState } from "react";
import { ChevronRight, UploadCloud, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { adminFetch } from "@/hooks/use-admin-session";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  buildProductTagsWithMeta,
  emptyProductDetailMeta,
  type ProductDetailMeta,
} from "@/lib/shop-product-meta";

export type ProductDraft = {
  id?: string;
  sku: string;
  name: string;
  description: string;
  category: string;
  priceTry: number;
  discountedPriceTry?: number;
  active: boolean;
  tagsText: string;
  imageUrl?: string;
  imageUrls?: string[];
  detailMeta: ProductDetailMeta;
};

const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "stone", label: "Taş" },
  { value: "oil", label: "Yağ" },
  { value: "bundle", label: "Paket" },
  { value: "other", label: "Diğer" },
];

const CATEGORY_SUGGESTIONS = [
  "dogal-tas",
  "dogal-tas/bileklik",
  "dogal-tas/kolye",
  "dogal-tas/kupe",
  "yag/aromaterapi",
  "paket/dogal-tas-yag",
  "diger",
];

const QUICK_CATEGORY_PRESETS: Array<{ label: string; value: string }> = [
  { label: "Doğal Taş", value: "dogal-tas" },
  { label: "Taş / Bileklik", value: "dogal-tas/bileklik" },
  { label: "Taş / Kolye", value: "dogal-tas/kolye" },
  { label: "Taş / Küpe", value: "dogal-tas/kupe" },
  { label: "Yağ", value: "yag" },
  { label: "Paket", value: "paket" },
];

function toCategorySlug(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/\s*\/\s*/g, "/")
    .replace(/[^a-z0-9\/-\s]+/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/\/+$/g, "")
    .replace(/^\//g, "");
}

type ProductMediaItem = {
  path: string;
  name: string;
  url: string;
  createdAt?: string | null;
};

export function NumerolojiProductEditor({
  draft,
  setDraft,
  onClose,
  onSave,
  isSaving,
}: {
  draft: ProductDraft;
  setDraft: (next: ProductDraft) => void;
  onClose: () => void;
  onSave: (payload: any) => void;
  isSaving: boolean;
}) {
  const isEdit = Boolean(draft.id);
  const title = isEdit ? "Ürünü Düzenle" : "Yeni Ürün Ekle";

  const fileRef = useRef<HTMLInputElement | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [libraryItems, setLibraryItems] = useState<ProductMediaItem[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);
  const [deletingPath, setDeletingPath] = useState<string | null>(null);

  const imageUrls = Array.from(new Set([...(draft.imageUrls ?? []), draft.imageUrl ?? ""].filter(Boolean))).slice(0, 5);
  const canUploadMore = imageUrls.length < 5;

  const formDisabled = Boolean(isSaving || isUploadingImage);

  const loadMediaLibrary = async () => {
    setLibraryLoading(true);
    setLibraryError(null);

    try {
      const res = await adminFetch("/api/admin/numeroloji/products/media");
      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Medya listesi alınamadı.");
      }

      const rows = Array.isArray(json?.items) ? json.items : [];
      setLibraryItems(
        rows
          .map((it: any) => ({
            path: String(it?.path ?? ""),
            name: String(it?.name ?? ""),
            url: String(it?.url ?? ""),
            createdAt: typeof it?.createdAt === "string" ? it.createdAt : null,
          }))
          .filter((it: ProductMediaItem) => it.path && it.url),
      );
    } catch (err) {
      setLibraryError(err instanceof Error ? err.message : "Medya listesi alınamadı.");
    } finally {
      setLibraryLoading(false);
    }
  };

  useEffect(() => {
    void loadMediaLibrary();
  }, []);

  const uploadImage = async (file: File) => {
    setImageError(null);

    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      setImageError("Dosya boyutu çok büyük (max 6MB).");
      return;
    }

    setIsUploadingImage(true);
    try {
      const dataBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = () => reject(new Error("Dosya okunamadı."));
        reader.onload = () => {
          const result = typeof reader.result === "string" ? reader.result : "";
          const comma = result.indexOf(",");
          if (comma === -1) return reject(new Error("Dosya okunamadı."));
          resolve(result.slice(comma + 1));
        };
        reader.readAsDataURL(file);
      });

      const res = await adminFetch("/api/admin/numeroloji/products/image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contentType: file.type || "image/jpeg",
          dataBase64,
          productId: draft.id,
          productSku: draft.sku,
          productName: draft.name,
        }),
      });

      const json = (await res.json().catch(() => null)) as any;
      if (!res.ok) {
        throw new Error(typeof json?.error === "string" ? json.error : "Resim yüklenemedi.");
      }

      const url = typeof json?.url === "string" ? json.url : "";
      if (!url) throw new Error("Resim URL alınamadı.");

      const nextImageUrls = Array.from(new Set([...(draft.imageUrls ?? []), draft.imageUrl ?? "", url].filter(Boolean))).slice(0, 5);
      setDraft({ ...draft, imageUrls: nextImageUrls, imageUrl: nextImageUrls[0] });
      await loadMediaLibrary();
    } catch (err) {
      setImageError(err instanceof Error ? err.message : "Resim yüklenemedi.");
    } finally {
      setIsUploadingImage(false);
    }
  };

  const savePayload = () => {
    const normalizedImages = Array.from(new Set([...(draft.imageUrls ?? []), draft.imageUrl ?? ""].filter(Boolean))).slice(0, 5);

    const normalizedTags = draft.tagsText
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

    onSave({
      ...draft,
      imageUrls: normalizedImages,
      imageUrl: normalizedImages[0],
      tags: buildProductTagsWithMeta(normalizedTags, draft.detailMeta),
    });
  };

  const baseInput =
    "w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-50";

  return (
    <div className="rounded-3xl border border-slate-200 bg-white/70 p-4 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/40 sm:p-6">
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400">
          <span>Ürün Yönetimi</span>
          <ChevronRight className="h-4 w-4" />
          <span>Ürünler</span>
          <ChevronRight className="h-4 w-4" />
          <span className="text-slate-900 dark:text-slate-100">{title}</span>
        </div>

        <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</div>
            <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Ürün bilgilerini düzenleyin ve kaydedin.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="outline"
              className="rounded-full bg-white/80 dark:bg-slate-950/40"
              onClick={onClose}
              disabled={formDisabled}
            >
              Vazgeç
            </Button>
            <Button className="rounded-full" onClick={savePayload} disabled={formDisabled}>
              {isSaving ? "Kaydediliyor..." : isUploadingImage ? "Resim Yükleniyor..." : "Kaydet"}
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Genel Bilgiler</div>

            <div className="mt-5 grid gap-5">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Ürün Adı</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Örn: Premium Numeroloji Analizi"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Açıklama</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[180px] resize-none")}
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  placeholder="Ürünün kısa açıklaması..."
                />
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Fiyat & Ürün Bilgisi</div>

            <div className="mt-5 grid gap-5 sm:grid-cols-2">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Fiyat (₺)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={String(draft.priceTry)}
                  onChange={(e) => setDraft({ ...draft, priceTry: Number(e.target.value || 0) })}
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">İndirimli Fiyat (₺)</Label>
                <Input
                  className={cn(baseInput)}
                  inputMode="numeric"
                  value={String(draft.discountedPriceTry ?? 0)}
                  onChange={(e) => {
                    const v = Number(e.target.value || 0);
                    setDraft({ ...draft, discountedPriceTry: v > 0 ? v : undefined });
                  }}
                  placeholder="İndirim yoksa 0 bırakın"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">SKU</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.sku}
                  onChange={(e) => setDraft({ ...draft, sku: e.target.value })}
                  placeholder="NUM-REP-001"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Kategori</Label>
                <div className="grid gap-2">
                  <select
                    className={cn(baseInput, "h-12")}
                    value={CATEGORY_OPTIONS.some((opt) => opt.value === draft.category) ? draft.category : "custom"}
                    onChange={(e) => {
                      const next = e.target.value;
                      if (next === "custom") return;
                      setDraft({ ...draft, category: next });
                    }}
                  >
                    {CATEGORY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                    <option value="custom">Özel kategori</option>
                  </select>

                  <Input
                    className={cn(baseInput)}
                    value={draft.category}
                    onChange={(e) => setDraft({ ...draft, category: toCategorySlug(e.target.value) })}
                    placeholder="Örn: dogal-tas/bileklik"
                    list="product-category-suggestions"
                  />
                  <datalist id="product-category-suggestions">
                    {CATEGORY_SUGGESTIONS.map((value) => (
                      <option key={value} value={value} />
                    ))}
                  </datalist>
                  <div className="text-xs text-slate-500 dark:text-slate-400">
                    Üst/alt kategori için <span className="font-semibold">/</span> kullanın. Örn: dogal-tas/bileklik
                  </div>

                  <div className="flex flex-wrap gap-2 pt-1">
                    {QUICK_CATEGORY_PRESETS.map((preset) => (
                      <Button
                        key={preset.value}
                        type="button"
                        variant="outline"
                        className="h-8 rounded-full bg-white/80 px-3 text-xs dark:bg-slate-950/40"
                        onClick={() => setDraft({ ...draft, category: preset.value })}
                        disabled={formDisabled}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid gap-2 sm:col-span-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Etiketler (virgülle)</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.tagsText}
                  onChange={(e) => setDraft({ ...draft, tagsText: e.target.value })}
                  placeholder="premium, rapor, dijital"
                />
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Ürün Detay İçeriği</div>
            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Ürün detay sayfasındaki özel bölümleri buradan yönetebilirsiniz.
            </div>

            <div className="mt-5 grid gap-5">
              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Enerji Rozeti</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.detailMeta?.energyBadgeText ?? emptyProductDetailMeta().energyBadgeText}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), energyBadgeText: e.target.value },
                    })
                  }
                  placeholder="Örn: Senin 8 Enerjin İçin Öneriliyor"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Taksit / Ödeme Notu</Label>
                <Input
                  className={cn(baseInput)}
                  value={draft.detailMeta?.installmentText ?? emptyProductDetailMeta().installmentText}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), installmentText: e.target.value },
                    })
                  }
                  placeholder="Örn: Kredi kartına 3 taksit seçeneği ile ₺283 x 3 ödeme imkanı"
                />
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Neden Bu Ürün? Başlık</Label>
                  <Input
                    className={cn(baseInput)}
                    value={draft.detailMeta?.whyThisProductTitle ?? emptyProductDetailMeta().whyThisProductTitle}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), whyThisProductTitle: e.target.value },
                      })
                    }
                    placeholder="Örn: Neden Bu Ürün?"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Önerilen Ürünler Başlığı</Label>
                  <Input
                    className={cn(baseInput)}
                    value={draft.detailMeta?.recommendedTitle ?? emptyProductDetailMeta().recommendedTitle}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), recommendedTitle: e.target.value },
                      })
                    }
                    placeholder="Örn: Bunlar da İlginizi Çekebilir"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Neden Bu Ürün? (metin)</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[120px] resize-none")}
                  value={draft.detailMeta?.whyThisProductText ?? emptyProductDetailMeta().whyThisProductText}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), whyThisProductText: e.target.value },
                    })
                  }
                  placeholder="8 rakamı gücü ve dengeyi temsil eder..."
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Ürün Özellikleri (her satır bir madde)</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[140px] resize-none")}
                  value={draft.detailMeta?.featuresText ?? emptyProductDetailMeta().featuresText}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), featuresText: e.target.value },
                    })
                  }
                  placeholder="%100 doğal taş\n925 ayar gümüş detay\n18-20 cm esnek ölçü"
                />
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Spiritüel Faydalar</Label>
                  <Textarea
                    className={cn(baseInput, "min-h-[120px] resize-none")}
                    value={draft.detailMeta?.spiritualBenefitsText ?? emptyProductDetailMeta().spiritualBenefitsText}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), spiritualBenefitsText: e.target.value },
                      })
                    }
                    placeholder="Ürünün spiritüel etkilerini yazın"
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Bakım ve Temizlik</Label>
                  <Textarea
                    className={cn(baseInput, "min-h-[120px] resize-none")}
                    value={draft.detailMeta?.careText ?? emptyProductDetailMeta().careText}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), careText: e.target.value },
                      })
                    }
                    placeholder="Nasıl temizlenir, nasıl saklanır"
                  />
                </div>
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Taşın Gücü Başlığı</Label>
                  <Input
                    className={cn(baseInput)}
                    value={draft.detailMeta?.stonePowerTitle ?? emptyProductDetailMeta().stonePowerTitle}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), stonePowerTitle: e.target.value },
                      })
                    }
                    placeholder="Örn: Taşın Gücü"
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Taş Etiketleri (virgülle)</Label>
                  <Input
                    className={cn(baseInput)}
                    value={draft.detailMeta?.stonePowerTagsText ?? emptyProductDetailMeta().stonePowerTagsText}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), stonePowerTagsText: e.target.value },
                      })
                    }
                    placeholder="Boğaz Çakrası, Koruma, İletişim"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Taşın Gücü Metni</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[120px] resize-none")}
                  value={draft.detailMeta?.stonePowerText ?? emptyProductDetailMeta().stonePowerText}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), stonePowerText: e.target.value },
                    })
                  }
                  placeholder="Ürünün enerji anlatımı"
                />
              </div>

              <div className="grid gap-2 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Yorum Başlığı</Label>
                  <Input
                    className={cn(baseInput)}
                    value={draft.detailMeta?.reviewsTitle ?? emptyProductDetailMeta().reviewsTitle}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), reviewsTitle: e.target.value },
                      })
                    }
                    placeholder="Örn: Aydınlanmış Ruhlar Yorumları"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Yorum Alt Başlığı</Label>
                  <Input
                    className={cn(baseInput)}
                    value={draft.detailMeta?.reviewsSubtitle ?? emptyProductDetailMeta().reviewsSubtitle}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), reviewsSubtitle: e.target.value },
                      })
                    }
                    placeholder="Örn: Kullanıcılarımızın deneyimleri"
                  />
                </div>
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Yorumlar (her satır: İsim|Mesaj|Puan)</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[140px] resize-none")}
                  value={draft.detailMeta?.reviewsText ?? emptyProductDetailMeta().reviewsText}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), reviewsText: e.target.value },
                    })
                  }
                  placeholder="Ayşe K.|Enerjisi çok iyi, çok memnun kaldım.|5"
                />
              </div>

              <div className="grid gap-2">
                <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Ek Galeri URL (her satır bir URL)</Label>
                <Textarea
                  className={cn(baseInput, "min-h-[120px] resize-none")}
                  value={draft.detailMeta?.galleryUrlsText ?? emptyProductDetailMeta().galleryUrlsText}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      detailMeta: { ...(draft.detailMeta ?? emptyProductDetailMeta()), galleryUrlsText: e.target.value },
                    })
                  }
                  placeholder="https://.../gorsel-2.webp&#10;https://.../gorsel-3.webp"
                />
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Buraya eklediğiniz görseller ürün detayındaki ana fotoğrafın altında thumbnail olarak görünür.
                </div>
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="flex items-center justify-between gap-3">
              <div className="text-base font-bold text-slate-900 dark:text-white">Ürün Medyası</div>
            </div>

            <div className="mt-5 grid gap-2">
              <Label className="text-sm font-semibold text-slate-700 dark:text-slate-300">Ürün Görselleri (max 5)</Label>

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  void uploadImage(f);
                  e.currentTarget.value = "";
                }}
              />

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full bg-white/80 gap-2 dark:bg-slate-950/40"
                  onClick={() => fileRef.current?.click()}
                  disabled={formDisabled || !canUploadMore}
                >
                  <UploadCloud className="h-4 w-4" />
                  {isUploadingImage ? "Yükleniyor..." : canUploadMore ? "Resim yükle" : "Maksimum 5 görsel"}
                </Button>
                <span className="text-xs text-slate-500 dark:text-slate-400">{imageUrls.length}/5</span>
              </div>

              {imageError ? <div className="text-xs text-destructive">{imageError}</div> : null}

              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  className={cn(baseInput)}
                  value={imageUrlInput}
                  onChange={(e) => setImageUrlInput(e.target.value)}
                  placeholder="Görsel URL yapıştır (opsiyonel)"
                  disabled={formDisabled || !canUploadMore}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl bg-white/80 dark:bg-slate-950/40"
                  disabled={formDisabled || !canUploadMore || !imageUrlInput.trim()}
                  onClick={() => {
                    const raw = imageUrlInput.trim();
                    if (!raw) return;
                    const nextImageUrls = Array.from(new Set([...(draft.imageUrls ?? []), draft.imageUrl ?? "", raw].filter(Boolean))).slice(0, 5);
                    setDraft({ ...draft, imageUrls: nextImageUrls, imageUrl: nextImageUrls[0] });
                    setImageUrlInput("");
                  }}
                >
                  URL Ekle
                </Button>
              </div>

              {imageUrls.length ? (
                <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {imageUrls.map((url, idx) => (
                    <div key={`${url}-${idx}`} className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40">
                      <img src={url} alt={draft.name || "Ürün görseli"} className="h-28 w-full object-cover" />
                      <button
                        type="button"
                        className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-black/60 text-white"
                        onClick={() => {
                          const nextImageUrls = imageUrls.filter((_, i) => i !== idx);
                          setDraft({ ...draft, imageUrls: nextImageUrls, imageUrl: nextImageUrls[0] });
                        }}
                        disabled={formDisabled}
                        aria-label="Görseli kaldır"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}

              <div className="mt-4 rounded-2xl border border-slate-200 p-4 dark:border-slate-800">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Medya Kütüphanesi</div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 rounded-full bg-white/80 px-3 text-xs dark:bg-slate-950/40"
                    onClick={() => void loadMediaLibrary()}
                    disabled={libraryLoading}
                  >
                    {libraryLoading ? "Yenileniyor..." : "Yenile"}
                  </Button>
                </div>

                {libraryError ? <div className="mb-3 text-xs text-destructive">{libraryError}</div> : null}

                {libraryLoading ? (
                  <div className="text-xs text-slate-500 dark:text-slate-400">Medya yükleniyor...</div>
                ) : libraryItems.length ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {libraryItems.map((item) => {
                      const selected = imageUrls.includes(item.url);
                      return (
                        <div key={item.path} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40">
                          <img src={item.url} alt={item.name || "Ürün görseli"} className="h-24 w-full object-cover" />
                          <div className="space-y-2 p-2">
                            <div className="truncate text-[10px] text-slate-500 dark:text-slate-400" title={item.path}>
                              {item.path}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                className="h-8 rounded-lg px-2 text-[10px]"
                                disabled={formDisabled || selected || !canUploadMore}
                                onClick={() => {
                                  const nextImageUrls = Array.from(new Set([...imageUrls, item.url])).slice(0, 5);
                                  setDraft({ ...draft, imageUrls: nextImageUrls, imageUrl: nextImageUrls[0] });
                                }}
                              >
                                {selected ? "Eklendi" : "Seç"}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                className="h-8 rounded-lg px-2 text-[10px] text-rose-600"
                                disabled={Boolean(deletingPath) || formDisabled}
                                onClick={async () => {
                                  setDeletingPath(item.path);
                                  setLibraryError(null);
                                  try {
                                    const res = await adminFetch("/api/admin/numeroloji/products/media/delete", {
                                      method: "POST",
                                      headers: { "Content-Type": "application/json" },
                                      body: JSON.stringify({ path: item.path }),
                                    });
                                    const json = (await res.json().catch(() => null)) as any;
                                    if (!res.ok) {
                                      throw new Error(typeof json?.error === "string" ? json.error : "Görsel silinemedi.");
                                    }

                                    const nextImageUrls = imageUrls.filter((u) => u !== item.url);
                                    setDraft({ ...draft, imageUrls: nextImageUrls, imageUrl: nextImageUrls[0] });
                                    await loadMediaLibrary();
                                  } catch (err) {
                                    setLibraryError(err instanceof Error ? err.message : "Görsel silinemedi.");
                                  } finally {
                                    setDeletingPath(null);
                                  }
                                }}
                              >
                                {deletingPath === item.path ? "Siliniyor" : "Sil"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-slate-500 dark:text-slate-400">Henüz yüklenmiş medya bulunmuyor.</div>
                )}
              </div>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">Durum</div>

            <div className="mt-4 flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">Ürün Aktif</div>
                <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Pasif ise müşteriler satın alamaz.
                </div>
              </div>

              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  className="peer sr-only"
                  checked={draft.active}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                />
                <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
              </label>
            </div>

            <div
              className={cn(
                "mt-4 inline-flex rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider",
                draft.active
                  ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300"
                  : "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300",
              )}
            >
              {draft.active ? "Yayında" : "Taslak"}
            </div>
          </Card>

          <Card className="rounded-3xl border-slate-200 p-6 dark:border-slate-800">
            <div className="text-base font-bold text-slate-900 dark:text-white">İpuçları</div>

            <div className="mt-4 rounded-2xl border border-blue-600/15 bg-blue-600/5 p-4 text-sm text-slate-700 dark:border-blue-500/20 dark:text-slate-200">
              <div className="font-semibold">Öneri</div>
              <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                Başlık ve açıklamayı kısa/öz tutarsanız ürün kartları daha temiz görünür.
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
